import React, {
  memo,
  RefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Checkbox,
  Slider,
  SliderChangeCallbackType,
  SliderChangeCommittedCallbackType,
} from "cfa-react-components";
import { AgGridReact } from "ag-grid-react";
import "./riskInformationTable.scss";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import I18n from "../../../../i18n/utils";
import {
  CsvCell,
  CsvExportParams,
  ExcelCell,
  ExcelExportParams,
  ExcelRow,
  ExcelStyle,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetRowIdFunc,
  GetRowIdParams,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  IsRowMaster,
  ITextFilterParams,
  ProcessRowGroupForExportParams,
  ValueGetterParams,
} from "ag-grid-community";
import OperatorHeader from "./OperatorHeader";
import { getUserPermissions } from "../../../utils/LocalStorage";
import CFASpinner from "../../Common/CFASpinnner/CFASpinner";
import RiskScoreHeader from "./riskScoreHeaderComponent";
import { Modal } from "reactstrap";
import RiskScoreMultiSetModal from "./riskScoreMultiSetModal";
import { useSelector } from "react-redux";
import { refundAnalysisEmailVerifiedSelector } from "../../../redux/features/refundAnalysisSlice";
import moment from "moment";
const RiskInformationTable: React.FC<any> = ({
  riskInformationList,
  frcList,
  userEmailValidated,
  detailFilters,
  feedbackModified,
  setGridApi,
  loading,
}: any): JSX.Element => {
  const gridRef: RefObject<any> = useRef();
  let parentNode: any;
  let detailNode: any;
  const [selectedRiskScore, setSelectedRiskScore] = useState(0);
  const [parentNodeForModal, setParentNodeForModal] = useState(null);
  const [displaySelectRiskModal, setDisplaySelectRiskModal] =
    useState<boolean>(false);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      //flex: 1,
      resizable: true,
      cellStyle: { wordBreak: "normal" },
    }),
    []
  );

  userEmailValidated = useSelector(refundAnalysisEmailVerifiedSelector);

  let tempList: any = [];
  for (const element of riskInformationList) {
    tempList.push({ ...element });
  }

  useEffect(() => {
    if (loading) {
      gridRef.current?.api.showLoadingOverlay();
    }
  }, [loading]);

  const showSelectRiskModal = (eventParams: any) => {
    if (userEmailValidated) {
      setParentNodeForModal(eventParams);
      setDisplaySelectRiskModal(true);
    }
  };

  const closeSelectRiskModal = () => {
    setDisplaySelectRiskModal(false);
  };

  const cell: (text: string, styleId?: string) => ExcelCell = (
    text: string,
    styleId?: string
  ) => {
    return {
      styleId: styleId,
      data: {
        type: /^\d+$/.test(text) ? "Number" : "String",
        value: String(text),
      },
    };
  };

  var getRows = (params: ProcessRowGroupForExportParams) => {
    let filteredDetailRows: any[] = [];

    for (let score of params.node.data.fsdmAnomalyScores) {
      let scoreShouldBeAdded = true;
      detailFilters.forEach((element: any) => {
        if (element.model) {
          switch (element.field) {
            case "modelProb": {
              if (+score.modelProb < +element.model.filter) {
                scoreShouldBeAdded = false;
              }
              break;
            }
            case "refundAnomalyScoreRead.riskLevel": {
              if (
                element.model.values.length === 1 &&
                score.refundAnomalyScoreRead.riskLevel > 0
              ) {
                scoreShouldBeAdded = false;
              } else if (
                element.model.values.length === 5 &&
                score.refundAnomalyScoreRead.riskLevel === 0
              ) {
                scoreShouldBeAdded = false;
              }
              break;
            }
            case "hasEmailedOperator": {
              if (element.model.values.length === 1) {
                if (
                  element.model.values[0] === true &&
                  score.refundAnomalyScoreRead.dateLastEmailOperator ===
                    undefined
                ) {
                  scoreShouldBeAdded = false;
                } else if (
                  element.model.values[0] === false &&
                  score.refundAnomalyScoreRead.dateLastEmailOperator !==
                    undefined
                ) {
                  scoreShouldBeAdded = false;
                }
              }
              break;
            }
            case "refundType": {
              if (
                element.model.filter === "Cash" &&
                score.refundType !== "Cash"
              ) {
                scoreShouldBeAdded = false;
              } else if (
                element.model.filter === "Credit" &&
                score.refundType !== "Credit"
              ) {
                scoreShouldBeAdded = false;
              }
              break;
            }
          }
          console.log(element);
        }
      });

      if (scoreShouldBeAdded) {
        filteredDetailRows.push(score);
      }
    }

    const rows = [
      {
        outlineLevel: 1,
        cells: [
          // @ts-ignore
          cell(""),
          cell("Risk Score", "header"),
          cell("Transaction #", "header"),
          cell("Date", "header"),
          cell("Time", "header"),
          cell("Risk Prob", "header"),
          cell("Rank of Risk", "header"),
          cell("Refund Amount", "header"),
          cell("Refund Type", "header"),
          cell("Model Explanations", "header"),
        ],
      },
    ].concat(
      ...filteredDetailRows.map((score: any) => [
        {
          outlineLevel: 1,
          cells: [
            cell(""),
            cell(score.refundAnomalyScoreRead.riskLevel, "body"),
            cell(score.transactionId + " ", "stringType"),
            cell(score.businessDate, "body"),
            cell(
              moment.utc(score.endTime, moment.ISO_8601).format("h:mm A"),
              "body"
            ),
            cell(score.modelProb, "body"),
            cell(score.modelDailyRank, "body"),
            cell(score.outputRefundAmount, "body"),
            cell(score.refundType, "body"),
            cell(score.effect1Explanation, "body"),
          ],
        },
      ])
    );
    return rows;
  };

  const defaultCsvExportParams = useMemo<CsvExportParams>(() => {
    return {
      getCustomContentBelowRow: (params) => {
        const rows = getRows(params);
        return rows.map((row) => row.cells) as CsvCell[][];
      },
    };
  }, []);
  const defaultExcelExportParams = useMemo<ExcelExportParams>(() => {
    return {
      getCustomContentBelowRow: (params) => getRows(params) as ExcelRow[],
      columnWidth: 120,
      fileName: "Sherlock.xlsx",
    };
  }, []);

  const CustomLoadingOverlay = (props: any) => {
    return (
      <div className="ag-overlay-loading-center">
        <CFASpinner></CFASpinner>
        <div> {props.loadingMessage} </div>
      </div>
    );
  };

  const loadingOverlayComponent = useMemo(() => {
    return CustomLoadingOverlay;
  }, []);

  const loadingOverlayComponentParams = useMemo(() => {
    return {
      loadingMessage: "One moment please...",
    };
  }, []);

  const isRowMaster = useMemo<IsRowMaster>(() => {
    return (dataItem: any) => {
      return dataItem.locationNumber == undefined ? true : false;
    };
  }, []);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    event.api.sizeColumnsToFit();
    event.columnApi.applyColumnState({
      state: [{ colId: "avgRiskRank", sort: "asc" }],
      defaultState: { sort: null },
    });
    setGridApi(gridRef.current.api);
  }, []);

  const listRef = useRef<any>();
  listRef.current = tempList;

  const getRowId = useMemo<GetRowIdFunc>(() => {
    return (params: GetRowIdParams) => {
      return params.data.locationNumber;
    };
  }, []);

  const checkUserPerms = () => {
    const treasuryPermissions = getUserPermissions();
    return (
      treasuryPermissions.hasOwnProperty("SHERLOCK_ADMIN") ||
      treasuryPermissions.hasOwnProperty("SUPERUSER")
    );
  };

  const getGridEditable = () => {
    return userEmailValidated && checkUserPerms();
  };

  const onClickSelectAllOperator = (eventParams: any) => {
    let countNotEmailed = 0;
    for (let score of parentNode.data.fsdmAnomalyScores) {
      let hasEmailed = score.refundAnomalyScoreRead.dateLastEmailOperator;
      if (
        (hasEmailed === undefined || hasEmailed === null) &&
        (score.refundAnomalyScoreRead.riskLevel === 3 ||
          score.refundAnomalyScoreRead.riskLevel === 4)
      ) {
        score.emailOperator = parentNode.data.emailOperator;
        countNotEmailed++;
      }
    }
    if (countNotEmailed > 0) {
      parentNode.data.emailOperator = !parentNode.data.emailOperator;
    }
    parentNode.detailNode.detailGridInfo.api?.forEachNodeAfterFilter(
      (node: any) => {
        if (
          (node.data.refundAnomalyScoreRead.dateLastEmailOperator ===
            undefined ||
            node.data.refundAnomalyScoreRead.dateLastEmailOperator === null) &&
          (node.data.refundAnomalyScoreRead.riskLevel === 3 ||
            node.data.refundAnomalyScoreRead.riskLevel === 4)
        ) {
          node.setDataValue("emailOperator", parentNode.data.emailOperator);
          addScoreToModifiedData(node.data);
        }
      }
    );

    gridRef.current.api.forEachDetailGridInfo((detailGridInfo: any) => {
      detailGridInfo.api.refreshHeader();
    });
  };

  const onClickSelectAllRiskScore = (eventParams: any) => {
    showSelectRiskModal(eventParams);
  };

  const marks = [
    {
      value: 1,
      label: "1",
    },
    {
      value: 2,
      label: "2",
    },
    {
      value: 3,
      label: "3",
    },
    {
      value: 4,
      label: "4",
    },
    {
      value: 5,
      label: "5",
    },
  ];

  let initialRiskInfoList: any[] = JSON.parse(
    JSON.stringify(riskInformationList)
  );

  const excelStyles = useMemo<ExcelStyle[]>(() => {
    return [
      {
        id: "numberType",
        dataType: "Number",
        numberFormat: {
          format: "0",
        },
      },
      {
        id: "currencyFormat",
        numberFormat: {
          format: "#,##0.00 €",
        },
      },
      {
        id: "negativeInBrackets",
        numberFormat: {
          format: "$[blue] #,##0;$ [red](#,##0)",
        },
      },
      {
        id: "booleanType",
        dataType: "Boolean",
      },
      {
        id: "stringType",
        dataType: "String",
      },
      {
        id: "dateType",
        dataType: "DateTime",
        numberFormat: { format: "mm/dd/yy;;;" },
      },
    ];
  }, []);

  const masterCellStyle = (newValue: any, rowData: any, headerName: any) => {
    let foundUnit = listRef.current.find(function (element: any) {
      if (rowData.locationNumber == element.locationNumber) {
        return element;
      }
    });

    if (foundUnit == undefined) {
      return { color: "black" };
    }

    if (foundUnit[headerName] == newValue) {
      return { color: "black" };
    } else {
      return { color: "red" };
    }
  };

  const detailCellStyle = (newValue: any, rowData: any, headerName: any) => {
    let foundUnit = listRef.current.find(function (element: any) {
      for (let score of element.refundAnomalyScores) {
        if (rowData.transactionId == score.transactionId) {
          return score;
        }
      }
    });
    if (foundUnit == undefined) {
      return { color: "black" };
    }

    if (foundUnit[headerName] == newValue) {
      return { color: "black" };
    } else {
      return { color: "red" };
    }
  };

  const compareToLaterDate = (
    oldValue: string,
    newValue: string,
    compareDate: string
  ) => {
    if (Date.parse(newValue) > Date.parse(compareDate)) {
      return oldValue;
    } else {
      return newValue;
    }
  };

  const compareToEarlierDate = (
    oldValue: string,
    newValue: string,
    compareDate: string
  ) => {
    if (Date.parse(newValue) < Date.parse(compareDate)) {
      return oldValue;
    } else {
      return newValue;
    }
  };

  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: I18n.t("RISK_ANALYSIS_LOCATION_NUMBER"),
      field: "locationNumber",
      filter: "agTextColumnFilter",
      editable: false,
      sortable: true,
      cellRenderer: "agGroupCellRenderer",
    },
    {
      headerName: I18n.t("RISK_ANALYSIS_OPERATOR_NAME"),
      field: "operatorName",
      filter: "agTextColumnFilter",
      editable: false,
      sortable: true,
    },
    {
      headerName: I18n.t("RISK_ANALYSIS_OPERATOR_LAST_EMAIL_DATE"),
      field: "dateLastEmailOperator",
      valueFormatter: function (params: { value: moment.MomentInput }) {
        return params.value == ""
          ? params.value
          : moment(params.value).format("MM/DD/YY");
      },
      filter: "agDateColumnFilter",
      editable: false,
      sortable: true,
      cellClass: "dateType",
    },
    {
      headerName: I18n.t("RISK_ANALYSIS_MAX_RISK_SCORE"),
      field: "modelProb",
      filter: "agNumberColumnFilter",
      editable: false,
      sortable: true,
    },
    {
      headerName: I18n.t("RISK_ANALYSIS_COUNT_HIGH_RISK_SCORES"),
      field: "countHighRiskScores",
      filter: "agNumberColumnFilter",
      cellRenderer: (params: any) => {
        let count: number = 0;
        for (let transaction of params.node.data.fsdmAnomalyScores) {
          let n = Number(transaction.modelProb);
          let researchStatusFilter = "";
          let operatorEmailedFilter = undefined;
          let transactionRanked =
            transaction.refundAnomalyScoreRead.riskLevel !== 0
              ? "Ranked"
              : "Unranked";
          let refundType = detailFilters.find(
            (filter: { field: string }) => filter.field === "refundType"
          );
          let maxRiskScore = detailFilters.find(
            (filter: { field: string }) => filter.field === "modelProb"
          );
          let researchStatusFilterObject = detailFilters.find(
            (filter: { field: string }) =>
              filter.field === "refundAnomalyScoreRead.riskLevel"
          );
          if (
            !researchStatusFilterObject?.model?.values.includes("0") &&
            researchStatusFilterObject?.model?.values.includes("5")
          ) {
            researchStatusFilter = "Ranked";
          } else if (
            researchStatusFilterObject?.model?.values.includes("0") &&
            researchStatusFilterObject?.model?.values.length === 1
          ) {
            researchStatusFilter = "Unranked";
          }
          let operatorEmailedFilterObject = detailFilters.find(
            (filter: { field: string }) => filter.field === "hasEmailedOperator"
          );

          if (
            operatorEmailedFilterObject?.model?.values.includes("true") &&
            operatorEmailedFilterObject?.model?.values.length === 1
          ) {
            operatorEmailedFilter = true;
          } else if (
            operatorEmailedFilterObject?.model?.values.includes("false") &&
            operatorEmailedFilterObject?.model?.values.length === 1
          ) {
            operatorEmailedFilter = false;
          }
          if (
            (refundType == undefined ||
              refundType?.model?.filter === "All" ||
              refundType?.model?.filter === "" ||
              refundType?.model?.filter === transaction.refundType) &&
            (maxRiskScore == undefined ||
              maxRiskScore?.model?.filter === "." ||
              maxRiskScore?.model?.filter === "" ||
              +n.toFixed(1 - Math.floor(Math.log(n) / Math.log(10))) >=
                +maxRiskScore?.model?.filter) &&
            (researchStatusFilter === "" ||
              researchStatusFilter === transactionRanked) &&
            (operatorEmailedFilter == undefined ||
              operatorEmailedFilter === transaction.hasEmailedOperator)
          ) {
            count++;
          }
        }
        return count;
      },
      editable: false,
      sortable: true,
    },
    {
      headerName: I18n.t("RISK_ANALYSIS_AVG_RISK_PROB"),
      field: "avgRiskProb",
      filter: "agNumberColumnFilter",
      editable: false,
      sortable: true,
    },
    {
      headerName: I18n.t("RISK_ANALYSIS_MAX_REFUND_AMOUNT"),
      field: "outputRefundAmount",
      filter: true,
      editable: false,
      sortable: true,
      valueFormatter: function (params: { value: any }) {
        if (params.value == undefined) {
          return "";
        }
        return "(" + params.value.toString().replace("-", "") + ")";
      },
    },
    {
      field: "financialConsultant",
      hide: true,
      filter: "agTextColumnFilter",
    },
    {
      field: "rcma",
      hide: true,
      filter: "agTextColumnFilter",
    },
    {
      field: "ranked",
      hide: true,
      filter: "agTextColumnFilter",
      suppressColumnsToolPanel: true,

      filterParams: {
        caseSensitive: true,
      },
    },
    {
      field: "refundType",
      hide: true,
      filter: "agTextColumnFilter",
      suppressColumnsToolPanel: true,

      filterParams: {
        caseSensitive: true,
      },
    },
    {
      field: "minTransDate",
      hide: true,
      suppressColumnsToolPanel: true,
      cellClass: "dateType",

      filter: true,
    },
    {
      field: "maxTransDate",
      hide: true,
      suppressColumnsToolPanel: true,
      cellClass: "dateType",

      filter: true,
    },
    {
      field: "hasUnEmailedTransactions",
      hide: true,
      suppressColumnsToolPanel: true,
      filter: true,
    },
    {
      field: "hasEmailedTransactions",
      hide: true,
      suppressColumnsToolPanel: true,
      filter: true,
    },
  ]);

  const detailCellRendererParams = useMemo(() => {
    return (params: ICellRendererParams) => {
      let res = {} as any;
      //set value of ID for parent row
      let detailScoreList = params.data.fsdmAnomalyScores;
      res.getDetailRowData = function (params: any) {
        let rowNodes: any[] = [];
        let assocRow;
        for (let assocId of params.data.fsdmAnomalyScores) {
          //set value for child row association
          assocRow = assocId.transactionId;
          //push row into node of the correct parent
          rowNodes.push(gridRef.current.api.getRowNode(assocRow));
        }
        params.successCallback(detailScoreList);
      };
      res.rowStyle = { backgroundColor: "black" };
      res.embedFullWidthRows = true;
      res.refreshStrategy = "rows";
      parentNode = params.node.parent;
      detailNode = params.node;
      res.detailGridOptions = {
        pagination: true,
        paginationPageSize: 15,
        rowSelection: "multiple",
        animateRows: false,
        context: {
          params: params,
          masterGrid: {
            node: params.node.parent,
            data: params.data,
          }, // attention!
        },
        detailRowAutoHeight: true,
        getRowHeight: function (params: any) {
          return 70;
        },
        onGridReady: function (event: GridReadyEvent) {
          // event.api.sizeColumnsToFit();
          event.columnApi.autoSizeAllColumns();
        },

        onFirstDataRendered: function (event: FirstDataRenderedEvent) {
          event.api.sizeColumnsToFit();
          event.columnApi.autoSizeAllColumns();
          setGridApi(gridRef.current.api);

          gridRef.current.api.forEachDetailGridInfo((detailGridInfo: any) => {
            detailGridInfo.columnApi.applyColumnState({
              state: [{ colId: "modelDailyRank", sort: "asc" }],
              defaultState: { sort: null },
            });
          });

          detailFilters.forEach((element: any) => {
            if (element.model) {
              gridRef.current.api.forEachDetailGridInfo(
                (detailGridInfo: any) => {
                  const detailFilterInstance =
                    detailGridInfo.api.getFilterInstance(element.field);
                  detailFilterInstance.setModel(element.model);
                  detailGridInfo.api.onFilterChanged();
                }
              );
              if (element.field === "modelProb") {
                let maxRiskScore: number = element.model.filter;
                const filterInstance = gridRef.current.api.getFilterInstance(
                  element.field
                );
                filterInstance.setModel({
                  filter: maxRiskScore,
                  filterType: "number",
                  type: "greaterThanOrEqual",
                });
                for (let row of gridRef.current.props.rowData) {
                  let count: number = 0;
                  for (let transaction of row.fsdmAnomalyScores) {
                    let n = Number(transaction.modelProb);
                    if (
                      +n.toFixed(1 - Math.floor(Math.log(n) / Math.log(10))) >=
                      +maxRiskScore
                    ) {
                      count++;
                    }
                  }
                  const parent = gridRef.current.api.getRowNode(
                    row.locationNumber
                  );
                  gridRef.current.api
                    .getRowNode(row.locationNumber)
                    .setDataValue("countHighRiskScores", count);
                }
                gridRef.current.api.onFilterChanged();
              }
            }
          });
        },
        onCellValueChanged: function (params: any) {
          addScoreToModifiedData(params.data);
          gridRef.current.api.forEachDetailGridInfo((detailGridInfo: any) => {
            detailGridInfo.api.refreshCells();
          });
          gridRef.current.api.onFilterChanged();
        },
        onFilterChanged: function (event: any) {
          gridRef.current.api.refreshCells();
        },

        columnDefs: [
          {
            headerName: I18n.t("RISK_ANALYSIS_EMAIL_OPERATOR"),
            field: "emailOperator",
            headerClass: "pink-header",
            cellParser: Boolean,
            filter: true,
            editable: getGridEditable(),
            sortable: true,
            headerComponent: OperatorHeader,
            headerComponentParams: {
              parentNode: parentNode,
              gridRef: gridRef,
              onSelectOperatorClick: onClickSelectAllOperator,
              userEmailValidated: userEmailValidated,
            },
            cellRenderer: (params: any) => {
              return (
                <Checkbox
                  disabled={
                    !getGridEditable() ||
                    params.data.refundAnomalyScoreRead.dateLastEmailOperator !=
                      null ||
                    !(
                      params.data.refundAnomalyScoreRead.riskLevel === 3 ||
                      params.data.refundAnomalyScoreRead.riskLevel === 4
                    )
                  }
                  checked={params.data.emailOperator}
                  onChange={() => {
                    params.data.emailOperator = !params.data.emailOperator;
                    addScoreToModifiedData(params.data);
                    gridRef.current.api.forEachDetailGridInfo(
                      (detailGridInfo: any) => {
                        detailGridInfo.api.refreshCells();
                      }
                    );
                    gridRef.current.api.refreshCells();
                  }}
                  style={{ marginLeft: "auto", marginRight: "auto" }}
                  crossOrigin={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              );
            },
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_RISK_LEVEL"),
            field: "refundAnomalyScoreRead.riskLevel",
            headerComponent: RiskScoreHeader,
            headerComponentParams: {
              parentNode: parentNode,
              gridRef: gridRef,
              onSelectRiskScoreClick: onClickSelectAllRiskScore,
            },
            cellRenderer: (params: any) => {
              const handleChange: SliderChangeCommittedCallbackType = (
                _event,
                newValue
              ) => {
                const parent = gridRef.current.api.getRowNode(
                  params.data.locationNumber
                );
                params.data.refundAnomalyScoreRead.riskLevel = newValue;
                let nodesWithOperatorChecked = 0;
                parent.detailNode.detailGridInfo.api?.forEachNodeAfterFilter(
                  (node: any) => {
                    if (node.data.transactionId == params.data.transactionId) {
                      if (newValue !== 3 && newValue !== 4) {
                        params.data.emailOperator = false;
                        node.setDataValue("emailOperator", false);
                        addScoreToModifiedData(node.data);
                      }
                    }
                    if (node.data.emailOperator == true) {
                      nodesWithOperatorChecked++;
                    }
                  }
                );
                if (nodesWithOperatorChecked == 0) {
                  parentNode.data.emailOperator = false;
                }

                addScoreToModifiedData(params.data);
                gridRef.current.api.forEachDetailGridInfo(
                  (detailGridInfo: any) => {
                    detailGridInfo.api.refreshCells({ force: true });
                    detailGridInfo.api.refreshHeader();
                  }
                );
                gridRef.current.api.refreshCells();
              };
              return (
                <Slider
                  disabled={!getGridEditable()}
                  min={0}
                  max={5}
                  step={1}
                  marks={marks}
                  value={params.data.refundAnomalyScoreRead.riskLevel}
                  style={{
                    marginLeft: "auto",
                    marginRight: "auto",
                    margin: "auto",
                    width: 70,
                  }}
                  crossOrigin={undefined}
                  onChange={handleChange}
                  color="secondary"
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              );
            },
            cellParser: Number,
            suppressSizeToFit: true,
            headerClass: "pink-header",
            filter: true,
            editable: getGridEditable(),
            sortable: true,
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_TRANSACTION_NUMBER"),
            headerClass: "read-only-header",
            cellClass: "numberType",
            field: "transactionId",
            filter: true,
            sortable: true,
            cellDataType: "text",
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_DATE"),
            field: "businessDate",
            valueFormatter: function (params: { value: moment.MomentInput }) {
              return moment(params.value).format("MM/DD/YY");
            },
            maxWidth: 200,
            filter: true,
            sortable: true,
            cellClass: "dateType",
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_TIME"),
            field: "endTime",
            valueFormatter: function (params: { value: moment.MomentInput }) {
              if (params.value == undefined) {
                return "";
              }
              return moment.utc(params.value, moment.ISO_8601).format("h:mm A");
            },
            maxWidth: 200,
            filter: true,
            sortable: true,
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_RISK_SCORE"),
            field: "modelProb",
            filter: "agNumberColumnFilter",
            sortable: true,
            cellDataType: "number",
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_RISK_RANK"),
            field: "modelDailyRank",
            filter: true,
            headerClass: "read-only-header",
            cellDataType: "number",
            sortable: true,
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_REFUND_AMOUNT"),
            field: "outputRefundAmount",
            editable: false,
            cellDataType: "number",
            sortable: true,
            valueFormatter: function (params: { value: any }) {
              if (params.value == undefined) {
                return "";
              }
              return "(" + params.value.toString().replace("-", "") + ")";
            },
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_REFUND_TYPE"),
            field: "refundType",
            editable: false,
            sortable: true,
            cellDataType: "text",
            filter: "agTextColumnFilter",
          },
          {
            headerName: I18n.t("RISK_ANALYSIS_EXPL"),
            field: "effect1Explanation",
            editable: false,
            cellDataType: "text",
            cellStyle: { whiteSpace: "pre" },
            suppressSizeToFit: true,
            resizable: true,
          },
          {
            field: "financialConsultant",
            hide: true,
            filter: "agTextColumnFilter",
          },
          {
            field: "hasEmailedOperator",
            hide: true,
            filter: true,
          },
        ],
      };
      return res;
    };
  }, []);

  function addScoreToModifiedData(params: any) {
    let matchedScore = null;
    const matchedParentRow = listRef.current.find(function (element: any) {
      for (let score of element.fsdmAnomalyScores)
        if (score.transactionId === params.transactionId) {
          matchedScore = score;
          return score;
        }
    });
    const locationList = feedbackModified.current.get(params.locationNumber);
    if (JSON.stringify(matchedScore) != JSON.stringify(params)) {
      if (locationList) {
        const index = locationList
          .map(function (e: any) {
            return e.transactionId;
          })
          .indexOf(params.transactionId);
        if (index > -1) {
          locationList[index] = params;
        } else {
          locationList.push(params);
        }
      } else {
        feedbackModified.current.set(params.locationNumber, [params]);
      }
    } else {
      if (locationList) {
        const index = locationList
          .map(function (e: any) {
            return e.transactionId;
          })
          .indexOf(params.transactionId);
        if (index > -1) {
          locationList.splice(index, 1);
        }
        if (locationList.length == 0) {
          feedbackModified.current.delete(params.locationNumber);
        }
      }
    }

    gridRef.current.api.refreshCells();
    gridRef.current.api.forEachDetailGridInfo((detailGridInfo: any) => {
      detailGridInfo.api.refreshCells();
    });
  }

  return (
    <div style={gridStyle} className="ag-theme-alpine">
      <AgGridReact
        masterDetail={true}
        ref={gridRef} // Ref for accessing Grid's API
        rowData={initialRiskInfoList} // Row Data for Rows
        getRowId={getRowId}
        excelStyles={excelStyles}
        detailRowAutoHeight={true}
        columnDefs={columnDefs} // Column Defs for Columns
        defaultColDef={defaultColDef} // Default Column Properties
        includeHiddenColumnsInQuickFilter={true}
        pagination={true}
        defaultCsvExportParams={defaultCsvExportParams}
        // @ts-ignore
        defaultExcelExportParams={defaultExcelExportParams}
        detailCellRendererParams={detailCellRendererParams}
        loadingOverlayComponent={loadingOverlayComponent}
        loadingOverlayComponentParams={loadingOverlayComponentParams}
        animateRows={false} // Optional - set to 'true' to have rows animate when sorted
        rowSelection="multiple" // Options - allows click selection of rows
        onGridReady={onGridReady}
        keepDetailRows={true}
      />
      <Modal
        isOpen={displaySelectRiskModal}
        className="modal-dialog"
        modalClassName="add-bu-modal"
      >
        <RiskScoreMultiSetModal
          close={closeSelectRiskModal}
          loading={loading}
          riskRecordsModified={feedbackModified}
          // unmountOnClose={true}
          userEmailValidated={userEmailValidated}
          parentNode={parentNodeForModal}
          gridRef={gridRef}
          addScoreToModifiedData={addScoreToModifiedData}
        />
      </Modal>
    </div>
  );
};

export default memo(RiskInformationTable);
